import { InputRequirement } from "./nodes/input-requirement";
import { ExecutionError } from "./node-execution";
import { SessionStatus } from "./session";
import { UiMessage } from "./ui-message";

export type AiChatNode = {
  messages: UiMessage[];
  id: string;
  name: string;
  type?: "reasoning" | "condition";
  status: "pending" | "started" | "finished" | "error";
  errorMessage?: string;
};

export type AiChatTurn = {
  turnId: string;
  nodes: AiChatNode[];
};

export type AiChatState = {
  sessionId: string;
  workflowId: string;
  status: SessionStatus;
  turns: AiChatTurn[];
  nextAction?: InputRequirement;
  error?: ExecutionError;
  metadata?: Record<string, unknown>;
  turnLookups: Record<string, number | undefined>;
  nodeLookups: Record<
    string,
    { turnIndex?: number; nodeIndex?: number } | undefined
  >;
  messageLookups: Record<
    string,
    | { turnIndex?: number; nodeIndex?: number; messageIndex?: number }
    | undefined
  >;
  workflowStartTime?: number;
  variables?: Record<string, unknown>;
  initialContext?: Record<string, unknown>;
  /**
   * Client-only; the server never sets it. True while the chat holds a turn
   * that nothing on this page is streaming: a transcript restored as still
   * running (a reload cut the request it was answering), or a send whose
   * connection died before the turn settled. The status still says the turn
   * is running, because on the server it may well be; this is what tells the
   * panel not to lock the composer on it and to offer Continue instead.
   */
  interrupted?: boolean;
};

/**
 * The statuses a chat holds while a turn is running on the server.
 */
export const isRunningChatStatus = (status: AiChatState["status"]) =>
  status === "active" || status === "processing";

export type AiFeature =
  | "analytics"
  | "create_test"
  | "custom_provider"
  | "pylon_ticket_analysis"
  | "modifications_chat"
  | "other"
  | "qa-bot";
